import { Link } from "@mui/material";

import { faqs, howItWorks, AnalyticsHeader } from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import LogoImage from "public/images/brooklynnets_flex/logo.png";
import SeatMap3dPreview from "public/images/brooklynnets_flex/bse-venue.jpg";
import BackgroundImage from "public/images/brooklynnets_flex/Brooklyn-Nets.jpg";

import type { TeamConfig } from "../types";

const BannerImage =
  "https://ss-leagues.s3.amazonaws.com/NBA%202024-25/brooklyn-nets-stadium.png";

const config: TeamConfig = {
  teamSchema: "brooklynnets_flex",
  browserTitle: "Brooklyn Nets Flex Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Brooklyn Nets",

  packs: {
    enabled: true,
    list: [
      {
        id: 1,
        amount: 3,
        offerMoreMessage:
          "Switch to a 6 Game Flex Plan and unlock exclusive savings!",
        title: "3 Game Flex Plan",
        description: "Pick your games and seat locations.",
        defaultSelected: true,
        buckets: [
          {
            id: 1,
            title: "Upcoming Games",
            description: "",
            max: 5,
            min: 3,
            displayAllEvents: true,
            remainingBucketLanguage: "hello/",
          },
        ],
        successMessage:
          "You're good to go! Add more games or select Continue to view seating options.",
        updatedAccountGroup: 0,
        customProgressBarLanguage: [
          {
            selectedEventCount: 0,
            progressBarText: "Add 3 games to continue.",
          },
          {
            selectedEventCount: 1,
            progressBarText: "Add 2 games to continue.",
          },
          {
            selectedEventCount: 2,
            progressBarText: "Add 1 more game to continue.",
          },
          {
            selectedEventCount: 5,
            progressBarText:
              "You're good to go! Click Continue to view seating options.",
          },
        ],
      },
      {
        id: 2,
        amount: 6,
        offerMoreMessage: "",
        title: "6 Game Flex Plan",
        description:
          "Unlock exclusive savings and pick your games and seat locations.",
        buckets: [
          {
            id: 1,
            title: "Upcoming Games",
            description: "",
            max: 7,
            min: 6,
            displayAllEvents: true,
          },
        ],
        successMessage:
          "You're good to go! Add 1 more game or select Continue to view seating options.",
        updatedAccountGroup: 1,
        customProgressBarLanguage: [
          {
            selectedEventCount: 0,
            progressBarText: "Add 6 games to unlock exclusive savings!",
          },
          {
            selectedEventCount: 1,
            progressBarText: "Add 5 games to continue.",
          },
          {
            selectedEventCount: 2,
            progressBarText: "Add 4 games to continue.",
          },
          {
            selectedEventCount: 3,
            progressBarText: "Add 3 games to continue.",
          },
          {
            selectedEventCount: 4,
            progressBarText: "Add 2 games to continue.",
          },
          {
            selectedEventCount: 5,
            progressBarText: "Add 1 game to continue.",
          },
          {
            selectedEventCount: 7,
            progressBarText:
              "You're good to go! Click Continue to view seating options.",
          },
        ],
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-00051-nets-sec",
    mapId: "main",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-TRKXNF4SF8",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/bse/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    findGamesBannerImage: BannerImage,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage,
    logoImage: LogoImage.src + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage.src,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: true,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideSectionFilter: true,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        For accessible seating, please call the ticket office at{" "}
        <Link
          href="tel:9176186700"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          (917) 618-6700
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#000000",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Add more games or select Continue to view seating options.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: "Your Brooklyn Nets tickets are confirmed!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
